import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";

export const PACKAGES = [
  {
    id: "basic-clean",
    name: "Basic Clean",
    price: "$60 – $110",
    tag: "Maintenance Wash",
    popular: false,
    description:
      "Essential exterior and interior upkeep to keep your vehicle fresh and protected.",
    features: [
      "Exterior Wash & Foam Bath",
      "Tire & Rim Deep Clean + Gloss",
      "Interior Wipe Down (Dash, Console, Trim)",
      "Interior Vacuum (Seats & Carpets)",
      "Streak-Free Windows Inside & Out",
    ],
  },
  {
    id: "full-detail",
    name: "Full Detail",
    price: "$115 – $175",
    tag: "Most Popular",
    popular: true,
    description:
      "Everything in the Basic Clean, plus an extra detail-oriented focus from bumper to bumper.",
    features: [
      "Everything in Basic Clean",
      "Deep Interior Scrub & Detailing",
      "Leather Conditioning / Fabric Spot Treatment",
      "Door Jambs, Vents & Crevice Detailing",
    ],
  },
  {
    id: "disaster-detail",
    name: "Disaster Detail",
    price: "$175 – $230",
    tag: "Heavy Duty Recovery",
    popular: false,
    description:
      "For vehicles that really need it. Packed with dog hair, bad stains, or months of grime — we go very thorough.",
    features: [
      "Everything in Full Detail",
      "Full Pet Hair Removal & Extraction",
      "Stain & Spot Extraction (Seats & Carpets)",
      "Odor Neutralization Treatment",
      "Extreme Dirt, Mud & Grime Recovery",
    ],
  },
];

const cardVariant = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function PricingPackages({ onBook }) {
  return (
    <section id="packages" data-testid="packages-section" className="relative py-24 sm:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-16 sm:mb-20"
        >
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400" data-testid="packages-eyebrow">
            01 — Packages
          </span>
          <h2 className="mt-4 font-syne font-extrabold uppercase tracking-tight leading-none text-4xl sm:text-6xl text-white">
            Pick Your <span className="text-amber-400">Shine</span>
          </h2>
          <p className="mt-5 max-w-xl text-slate-400 text-base sm:text-lg">
            Three levels of clean. Final price depends on vehicle size and condition —
            text us a photo for an exact quote.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {PACKAGES.map((pkg, i) => (
            <motion.article
              key={pkg.id}
              custom={i}
              variants={cardVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              data-testid={`package-card-${pkg.id}`}
              className={`group relative flex flex-col rounded-2xl border p-8 transition-[border-color,transform,background-color] duration-500 hover:-translate-y-2 ${
                pkg.popular
                  ? "bg-[#151a24] border-amber-500/50 shadow-[0_0_60px_rgba(245,158,11,0.12)]"
                  : "bg-[#12161F] border-white/10 hover:border-amber-500/30"
              }`}
            >
              <span
                data-testid={`package-tag-${pkg.id}`}
                className={`self-start font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 rounded-full border ${
                  pkg.popular
                    ? "text-black bg-amber-400 border-amber-400 font-semibold"
                    : "text-amber-400 border-amber-500/40"
                }`}
              >
                {pkg.tag}
              </span>

              <h3 className="mt-6 font-syne font-bold text-2xl sm:text-3xl uppercase text-white">
                {pkg.name}
              </h3>
              <p
                data-testid={`package-price-${pkg.id}`}
                className="mt-3 font-syne font-extrabold text-3xl sm:text-4xl text-amber-400"
              >
                {pkg.price}
              </p>
              <p className="mt-4 text-sm text-slate-400 leading-relaxed">{pkg.description}</p>

              <ul className="mt-7 flex flex-col gap-3.5 flex-1">
                {pkg.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
                    <Check size={16} strokeWidth={3} className="mt-0.5 text-amber-400 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => onBook(pkg)}
                data-testid={`package-book-${pkg.id}`}
                className={`mt-9 inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm px-6 py-3.5 transition-colors duration-300 ${
                  pkg.popular
                    ? "bg-amber-500 hover:bg-amber-400 text-black"
                    : "border border-white/15 text-white hover:border-amber-400/60 hover:bg-white/5"
                }`}
              >
                Book This Package
                <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </button>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
